import React, { useCallback, useEffect } from "react";

/** @jsx jsx */
import { jsx, css } from '@emotion/react';
import { vec2 } from "@tlaukkan/tsm";
import { GameElement } from "../../../../shared/gameElementTypes";
import { canGroupPromote, findAlikeElements, isRewardCell } from "../../../../shared/player";
import { GameClient } from "../../gameClient";
import { CapacityPanel } from "../CapacityPanel";
import { Market } from "../Market";
import { SellInfo } from "../SellInfo";
import { UIInfo } from "../UIInfo";
import { TutorialTooltip } from "../TutorialTooltip";
import { getDontShowTutorial, setDontShowTutorial } from "../../userSettings";
import { PlayerConstants, ShopConstants } from "../../../../shared/constants";
import { TutorialId } from "../TooltipInfo";
import { getSystem } from "../../../../shared/utils";
import { PlacementSystem } from "../../systems/placementSystem";
import { PreviewOnly } from "../../../../shared/components";

declare var gameClient: GameClient;

//Where tooltips go when there is no market to anchor them to
const fallbackTooltipStyle = css`
    position: absolute;
    bottom: 23%;
    left: 10%;
    display: flex;
    flex-direction: column;
    gap: 1vh;
    pointer-events: none;
`;

interface ShopScreenProps {
    capacity: number;
    currentFunds: number;
    experience: number;
    full: boolean;
    length: number;
    level: number;
    maxFunds: number;
    numCells: number;
    gainedMoney: number;
    offer?: GameElement[];
    rewardOffer?: GameElement[];
    draggedCell: boolean;
    currentState: string;
    round: number;
    showTutorial: (tutorials: TutorialId[]) => void;
    enabledButtons?: string;
}

export const ShopScreen = (props: ShopScreenProps) => {
    const {
        round,
        capacity,
        currentFunds,
        experience,
        full,
        length,
        level,
        maxFunds,
        numCells,
        gainedMoney,
        offer,
        rewardOffer,
        draggedCell,
        currentState,
        enabledButtons
    } = props;

    const [purchased, setPurchased] = React.useState(false);
    const [warningText, setWarningText] = React.useState('');
    const [infoText, setInfoText] = React.useState('');

    const onClearHover = () => {
        gameClient.setInspection(null);
        setInfoText('');
    };

    const onHoverAntibody = (antibodyType: GameElement, index: number) => {
        const position = new vec2(); //calculate this from index
        gameClient.setInspection({ element: antibodyType, position });
        const player = gameClient.getPlayer();
        if (isRewardCell(antibodyType)) {
            if (gameClient.stats.rewardsBought === 0) setInfoText('This is an exclusive reward for fully merging cells!');
        } else if (player) {
            const elements = findAlikeElements(player, antibodyType);
            if (canGroupPromote(elements, 1)) {
                if (gameClient.stats.cellsTripled === 0) setInfoText('Merge three Immune Cells of the same type to promote them!');
            } else if (elements.length >= 1) {
                if (gameClient.stats.cellsCombined === 0) setInfoText('Drag and drop Immune Cells of the same type to merge them!');
            }
        }
    };

    const onShowMarketButton = (showing: boolean) => {
        if (showing) {
            gameClient.showPlayer(gameClient.playerID);
        }
    };

    const sendWarning = (name: string) => {
        if (name === 'cantAfford') {
            setWarningText('Not enough money!');
        } else if (name === 'nospace') {
            setWarningText('No space for Immune Cell! Sell immune cells to make space or level up!');
        } else {
            setWarningText(name);
        }
    }

    const sendInfo = (name: string) => {
        setInfoText(name);
    }

    useEffect(() => {
        if (numCells === 0) {
            setInfoText('Buy Immune Cells from the market below to fight the virus!');
        }
    }, [numCells]);

    const [page, setPage] = React.useState(1);
    const items = offer?.concat(rewardOffer || []) || [];
    const prevPage = () => {
        setPage(prev => {
            return prev > 1 ? prev - 1 : Math.ceil(items.length / ShopConstants.CellsPerPage);
        });
    };

    const nextPage = () => {
        setPage(prev => {
            return prev > 1 ? prev - 1 : Math.ceil(items.length / ShopConstants.CellsPerPage) ? prev + 1 : 1;
        });
    };

    const isDraggingSingleCell = () => {
        if (draggedCell) {
            const placement = getSystem(gameClient.world, PlacementSystem);
            return placement && !placement.isMultiselecting();
        }
        const tutorialForceShow = gameClient.tutorialStateMachine.forceSellButtonShown;
        return tutorialForceShow;
    }

    const isDraggedPreview = () => {
        const placement = getSystem(gameClient.world, PlacementSystem);
        const entity = placement && placement.getCurrentlyDraggedEntity();
        return entity?.hasComponent(PreviewOnly)
    }

    const tooltips = <>
        {warningText !== '' && <UIInfo level={'Warning'} shouldTimeout={true} text={warningText} setText={setWarningText} />}
        {infoText !== '' && <UIInfo level={'Info'} shouldTimeout={false} text={infoText} setText={setInfoText} />}
    </>;

    return (<>
        {/*<Capacity numCells={numCells} purchased={purchased} setPurchased={setPurchased} capacity={capacity} />*/}
        <div>
            {gameClient.disableMarket && <div css={fallbackTooltipStyle}>{tooltips}</div>}
            {isDraggingSingleCell() && !gameClient.tutorialStateMachine.hideSellButton && <SellInfo isPreview={isDraggedPreview()} />}
            {!gameClient.disableMarket && <Market
                game={gameClient}
                tooltips={tooltips}
                offer={offer}
                rewardOffer={rewardOffer}
                experience={experience}
                level={level}
                gainedMoney={gainedMoney}
                setPurchased={setPurchased}
                hoverAntibody={onHoverAntibody}
                clearHoverAntibody={onClearHover}
                onShowMarketButton={onShowMarketButton}
                full={full}
                currentFunds={currentFunds}
                maxFunds={maxFunds}
                numCells={numCells}
                capacity={capacity}
                sendWarning={sendWarning}
                sendInfo={sendInfo}
                currentState={currentState}
                draggedCell={draggedCell}
                page={page}
                setPage={setPage}
                prevPage={prevPage}
                nextPage={nextPage}
                enabledButtons={enabledButtons}
            />}
        </div>
    </>);
};
